import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import InputIconByKey from "./InputIconByKey";
import RequiredAsteriskTooltip from "./RequiredAsteriskTooltip";
import useDebounced from "../../../hooks/useDebounced";
import { IconKey } from "../../../types/system/icons";

interface OptionItem {
  id: number;
  name: string;
}

interface MultiSearchSelectProps {
  label: string;
  name: string;
  options: OptionItem[];
  selectedValues: number[];
  onChange: (selectedIds: number[]) => void;
  iconKey?: IconKey;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  searchPlaceholder?: string;
}

const MultiSearchSelect: React.FC<MultiSearchSelectProps> = ({
  label,
  name,
  options,
  selectedValues,
  onChange,
  iconKey,
  error,
  required = false,
  disabled = false,
  searchPlaceholder = "Buscar...",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearch = useDebounced(searchTerm, 300);
  const [filteredOptions, setFilteredOptions] = useState<OptionItem[]>(options);

  useEffect(() => {
    if (debouncedSearch.trim() === "") {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter((opt) =>
          opt.name.toLowerCase().includes(debouncedSearch.toLowerCase())
        )
      );
    }
  }, [debouncedSearch, options]);

  // 🔒 cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🎯 al abrir, foco inmediato en el buscador
  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    } else {
      setSearchTerm("");
    }
  }, [isOpen]);

  const toggleOpen = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const handleSelect = (id: number) => {
    if (selectedValues.includes(id)) {
      onChange(selectedValues.filter((val) => val !== id));
    } else {
      onChange([...selectedValues, id]);
    }
  };

  const removeAll = () => onChange([]);
  const removeOne = (id: number) =>
    onChange(selectedValues.filter((val) => val !== id));

  const selectedOptions = options.filter((opt) =>
    selectedValues.includes(opt.id)
  );

  return (
    <div className="relative mb-5" ref={containerRef}>
      <label className="mb-2.5 flex items-center gap-1 text-sm font-medium text-black dark:text-white">
        {label}
        {required && <RequiredAsteriskTooltip delay={400} />}
      </label>

      {/* Contenedor principal */}
      <div
        className={`
          bg-gray-1 flex min-h-[50px] items-stretch overflow-hidden rounded-xl
          border border-stroke transition-all duration-200
          focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10
          dark:border-strokedark dark:bg-meta-4
          ${disabled ? 'cursor-not-allowed opacity-70' : ''}
        `}
      >
        {iconKey && (
          <div className="flex items-center justify-center border-r border-stroke bg-gray-2 px-4 dark:border-strokedark dark:bg-boxdark-2">
            <InputIconByKey iconKey={iconKey} />
          </div>
        )}

        {/* Chips seleccionados */}
        <div
          className={`flex max-h-32 flex-1 flex-wrap items-center gap-2 overflow-y-auto px-4 py-2.5 text-sm ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
          onClick={toggleOpen}
        >
          {selectedOptions.length > 0 ? (
            selectedOptions.map((opt) => (
              <span
                key={opt.id}
                className="flex items-center gap-1.5 rounded-full bg-primary/10 py-1 pl-3 pr-1.5 text-xs font-medium text-primary dark:bg-primary/15"
              >
                {opt.name}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeOne(opt.id);
                    }}
                    className="rounded-full p-0.5 text-primary/70 transition hover:bg-primary/20 hover:text-primary"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))
          ) : (
            <span className="text-body dark:text-bodydark2">
              Seleccionar...
            </span>
          )}
        </div>

        {/* Botón limpiar */}
        {selectedOptions.length > 0 && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeAll();
            }}
            className="my-2 mr-2 shrink-0 rounded-md px-2.5 py-1 text-xs font-medium text-danger transition hover:bg-danger/10"
          >
            Limpiar
          </button>
        )}

        {/* Icono chevron */}
        <span
          role="button"
          tabIndex={0}
          onClick={toggleOpen}
          className={`
            flex items-center pr-4 transition-transform duration-300 ease-in-out
            ${disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:opacity-80'}
            ${isOpen ? 'rotate-180 text-primary' : 'rotate-0 text-body dark:text-bodydark2'}
          `}
        >
          <ChevronDown className="h-5 w-5" />
        </span>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 top-full z-999999 mt-1 w-full rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          {/* Acciones superiores */}
          <div className="flex items-center justify-between gap-2 border-b border-stroke p-2 dark:border-strokedark">
            <button
              type="button"
              className="rounded-md px-2.5 py-1 text-xs font-medium text-primary transition hover:bg-primary/10"
              onClick={(e) => {
                e.stopPropagation();
                onChange(options.map((opt) => opt.id));
              }}
            >
              Seleccionar todos
            </button>
            <button
              type="button"
              className="rounded-md px-2.5 py-1 text-xs font-medium text-danger transition hover:bg-danger/10"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            >
              Quitar todos
            </button>
          </div>

          {/* Buscador */}
          <input
            ref={searchInputRef}
            type="text"
            name={name}
            placeholder={searchPlaceholder}
            className="w-full border-b border-stroke px-4 py-2.5 text-sm text-black outline-none placeholder:text-body dark:border-strokedark dark:bg-boxdark dark:text-white dark:placeholder:text-bodydark2"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Opciones */}
          <ul className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const checked = selectedValues.includes(opt.id);
                return (
                  <li
                    key={opt.id}
                    onClick={() => handleSelect(opt.id)}
                    className={`
                      flex cursor-pointer items-center justify-between gap-2
                      border-b border-stroke px-4 py-2.5 text-sm transition last:border-b-0
                      hover:bg-primary hover:text-white
                      dark:border-strokedark
                      ${checked ? 'bg-primary/10 text-primary dark:bg-primary/15' : 'text-black dark:text-white'}
                    `}
                  >
                    {opt.name}
                    {checked && <Check className="h-4 w-4 shrink-0" />}
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-3 text-sm text-body dark:text-bodydark2">
                No se encontraron resultados
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Error */}
      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};

export default MultiSearchSelect;
